import { useTracking } from 'react-tracking';
import Media from 'react-media';

import {
  Grid,
  Text,
  Heading,
  LoadingSpinner,
  Alert,
  useMount,
  useTheme,
} from '@simplywallst/ui-core';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import { HoldingsTableLoading } from '../../../../components/HoldingsTable';
import { PortfolioSettings } from '../PortfolioSettings';
import { PortfolioPageHeader } from '../../../../components/PortfolioPageHeader';
import { PortfolioOverviewLoadingState } from '../../../../components/PortfolioOverview';

import { ImportingMessage, TableHeader } from './styled';

type Props = {
  portfolio: Portfolio;
};

const ImportingState = ({ portfolio }: Props) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useUserAgent();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'importingState',
  });

  useMount(() => {
    trackEvent({ action: 'landed' });
  });

  return (
    <Track>
      <PortfolioPageHeader
        portfolio={portfolio}
        templateRows="auto"
        templateColumns="auto min-content"
        templateColumnsMd="auto auto"
        justifyContentMd="space-between"
      >
        <Media
          defaultMatches={isMobile || isTablet}
          query={{ maxWidth: theme.mdMinRaw }}
        >
          <PortfolioSettings portfolio={portfolio} />
        </Media>
        <Grid templateColumns="max-content auto" alignItems="center" gap="8px">
          <Grid
            templateColumns="max-content auto"
            alignItems="center"
            gap="8px"
          >
            <Text typography="tiny" color="whiteRGBA" opacity={0.3}>
              Updating
            </Text>
            <LoadingSpinner light />
          </Grid>
          <Media
            defaultMatches={isDesktop}
            query={{ minWidth: theme.mdMinRaw }}
          >
            <PortfolioSettings portfolio={portfolio} />
          </Media>
        </Grid>
      </PortfolioPageHeader>
      <section>
        <ImportingMessage>
          <Alert styleType="loading" data-cy-id="alert-resyncing-message">
            Your portfolio data is being imported. This might take a few
            minutes. Please check back in later.
          </Alert>
        </ImportingMessage>
        <PortfolioOverviewLoadingState />
      </section>
      <section>
        <TableHeader>
          <Heading as="h2" typography="baseStrong">
            Holdings
          </Heading>
        </TableHeader>
        <HoldingsTableLoading />
      </section>
    </Track>
  );
};

export default ImportingState;
